import { useState, useEffect, useRef } from "react";
import { format, parse, isValid, setMonth, setYear } from "date-fns";
import { CalendarIcon, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { InlineLoading } from "@/components/ui/loading-states";

const TIMELINE_LABELS: Record<string, string> = {
  within_1_year: "less than 1 year",
  "1_to_2_years": "1–2 years",
  "2_to_3_years": "2–3 years",
  "3_to_5_years": "3–5 years",
  "5_to_10_years": "5–10 years",
  over_10_years: "more than 10 years",
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

type Variant = "specific" | "unsure" | "skip";

interface RecallBridgeScreenProps {
  onContinue: (targetDate: string | null) => void;
}

function FutureDatePicker({
  date,
  onDateChange,
}: {
  date: Date | undefined;
  onDateChange: (d: Date | undefined) => void;
}) {
  const [calendarMonth, setCalendarMonth] = useState<Date>(date || new Date());
  const [textValue, setTextValue] = useState(date ? format(date, "MM/dd/yyyy") : "");
  const [popoverOpen, setPopoverOpen] = useState(false);

  const today = new Date();
  const currentYear = today.getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => currentYear + i);

  const handleTextChange = (val: string) => {
    setTextValue(val);

    // Try parsing common formats
    for (const fmt of ["MM/dd/yyyy", "yyyy-MM-dd", "MM-dd-yyyy"]) {
      const parsed = parse(val, fmt, new Date());
      if (isValid(parsed) && parsed >= today) {
        onDateChange(parsed);
        setCalendarMonth(parsed);
        return;
      }
    }
  };

  const handleCalendarSelect = (d: Date | undefined) => {
    onDateChange(d);
    if (d) {
      setTextValue(format(d, "MM/dd/yyyy"));
      setPopoverOpen(false);
    }
  };

  const handleMonthSelect = (monthStr: string) => {
    const newMonth = setMonth(calendarMonth, parseInt(monthStr));
    setCalendarMonth(newMonth);
  };

  const handleYearSelect = (yearStr: string) => {
    const newYear = setYear(calendarMonth, parseInt(yearStr));
    setCalendarMonth(newYear);
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            type="text"
            placeholder="MM/DD/YYYY"
            value={textValue}
            onChange={(e) => handleTextChange(e.target.value)}
            className="min-h-[48px] pr-10"
          />
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-foreground transition-colors"
              >
                <CalendarIcon className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <div className="p-3 space-y-3">
                {/* Month/Year fast navigation */}
                <div className="flex items-center gap-2 justify-between">
                  <Select
                    value={calendarMonth.getMonth().toString()}
                    onValueChange={handleMonthSelect}
                  >
                    <SelectTrigger className="h-8 w-[130px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, i) => (
                        <SelectItem key={i} value={i.toString()}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={calendarMonth.getFullYear().toString()}
                    onValueChange={handleYearSelect}
                  >
                    <SelectTrigger className="h-8 w-[90px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={y.toString()}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={handleCalendarSelect}
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  disabled={(d) => d < today}
                  initialFocus
                  className={cn("p-0 pointer-events-auto")}
                />
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      {textValue && !date && (
        <p className="text-xs text-muted-foreground">
          Enter a future date as MM/DD/YYYY
        </p>
      )}
    </div>
  );
}

export function RecallBridgeScreen({ onContinue }: RecallBridgeScreenProps) {
  const { user } = useAuth();
  const [variant, setVariant] = useState<Variant | null>(null);
  const [timelineLabel, setTimelineLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState<Date | undefined>();
  const [showDatePicker, setShowDatePicker] = useState(false);

  useEffect(() => {
    async function loadA1Answer() {
      if (!user?.id) {
        setVariant("skip");
        setLoading(false);
        return;
      }

      try {
        const { data } = await supabase
          .from("assessment_progress")
          .select("answers")
          .eq("user_id", user.id)
          .eq("module_id", "owner-readiness")
          .maybeSingle();

        const answers = data?.answers as Record<string, unknown> | null;
        const timelineValue = answers?.succession_timeline_years as string | undefined;

        if (!timelineValue) {
          setVariant("skip");
        } else if (timelineValue === "unsure") {
          setVariant("unsure");
        } else if (TIMELINE_LABELS[timelineValue]) {
          setVariant("specific");
          setTimelineLabel(TIMELINE_LABELS[timelineValue]);
        } else {
          setVariant("skip");
        }
      } catch {
        setVariant("skip");
      }
      setLoading(false);
    }

    loadA1Answer();
  }, [user?.id]);

  // Variant C — auto-skip
  useEffect(() => {
    if (!loading && variant === "skip") {
      onContinue(null);
    }
  }, [loading, variant, onContinue]);

  if (loading || variant === "skip") {
    return (
      <div className="h-full flex items-center justify-center">
        <InlineLoading size="lg" text="Loading..." />
      </div>
    );
  }

  const handleConfirmDate = () => {
    if (date) {
      onContinue(format(date, "yyyy-MM-dd"));
    }
  };

  const handleSkipDate = () => {
    onContinue(null);
  };

  return (
    <div className="max-w-2xl mx-auto w-full">
      <div className="space-y-6">
        {/* Heading */}
        <div>
          <h3 className="font-semibold text-lg text-foreground mb-2">
            {variant === "specific"
              ? "Your Exit Timeline"
              : "Set a Working Target Date"}
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {variant === "specific"
              ? `In your mySuccession Snapshot, you indicated your exit is ${timelineLabel} away. Do you have a specific target date in mind?`
              : "Let's set a working target exit date — this can always be updated in your dashboard."}
          </p>
        </div>

        {/* Variant A: Yes/Not yet toggle before date picker */}
        {variant === "specific" && !showDatePicker && (
          <div className="flex gap-3">
            <Button
              onClick={() => setShowDatePicker(true)}
              className="flex-1"
            >
              Yes, I have a date
            </Button>
            <Button
              variant="outline"
              onClick={handleSkipDate}
              className="flex-1"
            >
              Not yet
            </Button>
          </div>
        )}

        {/* Date picker — shown for Variant B always, Variant A after "Yes" */}
        {(variant === "unsure" || showDatePicker) && (
          <div className="space-y-4">
            <FutureDatePicker date={date} onDateChange={setDate} />

            <Button
              onClick={handleConfirmDate}
              disabled={!date}
              className="w-full min-h-[48px] font-medium text-base"
            >
              Continue
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>

            {variant === "unsure" && (
              <button
                onClick={handleSkipDate}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
              >
                Skip for now
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
